import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Star, User, Calendar, Camera, ThumbsUp, MessageSquare, Loader2 } from 'lucide-react';
import { useProductReviews, useReviewStats } from '../hooks/useProductReviews';
import ReviewForm from './ReviewForm';
import { getProductImageUrl } from '../lib/utils';

interface ProductReviewsSectionProps {
  productId: string;
  productName: string;
}

const ProductReviewsSection: React.FC<ProductReviewsSectionProps> = ({
  productId,
  productName
}) => {
  const { reviews, loading, error, refreshReviews } = useProductReviews(productId);
  const { stats, loading: statsLoading } = useReviewStats(productId);
  const [showReviewForm, setShowReviewForm] = useState(false);

  const handleReviewSubmitted = () => {
    setShowReviewForm(false);
    // Refresh reviews after submission (though new review won't show until approved)
    refreshReviews();
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'h-3 w-3',
      md: 'h-4 w-4',
      lg: 'h-5 w-5'
    };

    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`${sizeClasses[size]} ${
          index < Math.floor(rating) ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading || statsLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-red-700 mr-3" />
        <span className="text-gray-600">Loading reviews...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Error loading reviews: {error}</p>
        <button 
          onClick={refreshReviews}
          className="btn btn-primary px-6 py-2"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Review Statistics */}
      {stats.totalReviews > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gray-50 rounded-lg p-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start space-x-2 mb-2">
                <div className="flex space-x-1">
                  {renderStars(stats.averageRating, 'lg')}
                </div>
                <span className="text-2xl font-bold text-gray-900">
                  {stats.averageRating.toFixed(1)}
                </span>
              </div>
              <p className="text-gray-600">
                Based on {stats.totalReviews} review{stats.totalReviews !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="space-y-2">
              {stats.ratingDistribution.map((count, index) => {
                const starCount = index + 1;
                const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
                
                return (
                  <div key={starCount} className="flex items-center space-x-2 text-sm">
                    <span className="w-8 text-gray-600">{starCount}★</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="w-8 text-gray-600 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      )}

      {/* Write Review Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-gray-900">
          Customer Reviews {stats.totalReviews > 0 && `(${stats.totalReviews})`}
        </h3>
        <button
          onClick={() => setShowReviewForm(!showReviewForm)}
          className="btn btn-primary py-2 px-4 flex items-center space-x-2"
        >
          <MessageSquare className="h-4 w-4" />
          <span>{showReviewForm ? 'Cancel' : 'Write Review'}</span>
        </button>
      </div>

      {/* Review Form */}
      {showReviewForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ReviewForm
            productId={productId}
            productName={productName}
            onReviewSubmitted={handleReviewSubmitted}
          />
        </motion.div>
      )}

      {/* Reviews List */}
      {reviews.length > 0 ? (
        <div className="space-y-6">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="h-6 w-6 text-red-700" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{review.reviewer_name}</h4>
                      <div className="flex items-center space-x-2">
                        <div className="flex space-x-1">
                          {renderStars(review.rating)}
                        </div>
                        {review.is_verified && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            Verified Purchase
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(review.created_at)}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {review.review_text}
                  </p>
                  
                  {/* Review Image */}
                  {review.image_url && (
                    <div className="mb-4">
                      <img
                        src={getProductImageUrl(review.image_url)}
                        alt="Customer review"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                  )}
                  
                  {/* Review Actions */}
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <button className="flex items-center space-x-1 hover:text-red-700 transition-colors">
                      <ThumbsUp className="h-4 w-4" />
                      <span>Helpful</span>
                    </button>
                    {review.image_url && (
                      <div className="flex items-center space-x-1">
                        <Camera className="h-4 w-4" />
                        <span>Photo included</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Reviews Yet</h3>
          <p className="text-gray-600 mb-4">
            Be the first to review this product and help other customers make informed decisions.
          </p>
          <button
            onClick={() => setShowReviewForm(true)}
            className="btn btn-primary py-2 px-6"
          >
            Write First Review
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductReviewsSection;